import { inject, Injectable } from '@angular/core';
import { IQBoostViewStore } from '@maximizer/ai/data-access';
import {
  AddressBookService,
  AppointmentService,
  NoteService,
  SessionService,
  TaskService,
} from '@maximizer/core/shared/data-access';
import { Entity, Octopus } from '@maximizer/core/shared/domain';
import { OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { TimelineService } from '@maximizer/core/timeline/data-access';
import { escapeHtml, formatIsoDateOnly } from '@maximizer/shared/util';
import {
  addDays,
  addMinutes,
  formatISO,
  isSameDay,
  roundToNearestMinutes,
  set,
  startOfDay,
} from 'date-fns';
import { catchError, map, Observable, shareReplay, switchMap } from 'rxjs';
import { AnswerAction } from '../../answer-widgets/answer-widget.model';
import { AI_GENERATED_CATEGORY } from '../../classes/iqboost';

/** When an action lands, resolved from the reader's clock the moment its card is drawn. */
export interface AnswerActionWhen {
  /** The day, at midnight for a task or a note and at the start time for an appointment. */
  start: Date;
  /** Whether `start` carries a time of day, as an appointment's does. */
  hasTime: boolean;
}

/** Where an appointment with no time from the data is set on a future day. */
export const DEFAULT_APPOINTMENT_TIME = '09:00';
export const APPOINTMENT_MINUTES = 30;

/**
 * The day the data named, else today plus `inDays`; an appointment at its time, else nine, or the
 * next half hour once nine has gone today. A day in the past is kept for the advisor to change.
 */
export function resolveWhen(
  action: AnswerAction,
  now = new Date(),
): AnswerActionWhen {
  const day = action.date ?? addDays(startOfDay(now), action.inDays ?? 0);
  if (action.type !== 'appointment') {
    return { start: day, hasTime: false };
  }

  const [hours, minutes] = (action.time ?? DEFAULT_APPOINTMENT_TIME)
    .split(':')
    .map(Number);
  const start = set(day, { hours, minutes, seconds: 0, milliseconds: 0 });
  const defaultPassed = !action.time && isSameDay(start, now) && start < now;

  return {
    start: defaultPassed
      ? roundToNearestMinutes(now, { nearestTo: 30, roundingMethod: 'ceil' })
      : start,
    hasTime: true,
  };
}

/**
 * A note stamped `at` the moment it is written, as classic stamps one, with the title and then the
 * description as its text.
 */
export function toNote(
  action: AnswerAction,
  at = new Date(),
): Octopus.NoteCreate {
  return {
    Key: null,
    ParentKey: action.withEntityKey ?? null,
    Category: AI_GENERATED_CATEGORY,
    RichText: richText(action),
    Type: { Key: Octopus.DefaultNotes.MANUAL },
    DateTime: at,
  } as Octopus.NoteCreate;
}

/**
 * A task due on a day, with no time: a date-only `DateTime`, as the core task dialog sends one.
 * A lead's task goes on `LeadKey`, anything else on `AbEntryKey`; neither makes a personal to-do.
 */
export function toTask(action: AnswerAction, when: AnswerActionWhen): Entity {
  const task: Entity = {
    Key: null,
    Activity: noteText(action),
    DateTime: formatIsoDateOnly(when.start),
    WithKey: null,
  };

  if (action.withEntityKey) {
    task[action.withType === 'lead' ? 'LeadKey' : 'AbEntryKey'] =
      action.withEntityKey;
  }

  return task;
}

/** Half an hour from `when.start`, on the entry the action names; the API fills the rest. */
export function toAppointment(
  action: AnswerAction,
  when: AnswerActionWhen,
  organizerKey: string,
): Octopus.AppointmentCreate {
  const entry = { Key: { Value: action.withEntityKey ?? '' } };
  const lead = action.withType === 'lead';

  return {
    Key: null,
    Subject: action.title,
    StartDate: formatISO(when.start),
    EndDate: formatISO(addMinutes(when.start, APPOINTMENT_MINUTES)),
    Description: action.description ?? null,
    Organizer: organizerKey,
    AbEntries: lead ? [] : [entry],
    Leads: lead ? [entry] : [],
  } as Octopus.AppointmentCreate;
}

// The timeline renders `RichText` as HTML, so the text is escaped and its line break made one.
function richText(action: AnswerAction): string {
  return escapeHtml(noteText(action)).replaceAll('\n', '<br>');
}

function noteText(action: AnswerAction): string {
  return action.description
    ? `${action.title}\n${action.description}`
    : action.title;
}

/**
 * Creates the record a suggested action describes, opens it, and names the entry it is for: the
 * one place the cards touch the CRM, through the services classic's create flow uses.
 */
@Injectable({ providedIn: 'root' })
export class SuggestedActionService {
  private readonly notes = inject(NoteService);
  private readonly tasks = inject(TaskService);
  private readonly appointments = inject(AppointmentService);
  private readonly addressBook = inject(AddressBookService);
  private readonly session = inject(SessionService);
  private readonly timeline = inject(TimelineService);
  private readonly store = inject(IQBoostViewStore);
  private readonly names = new Map<string, Observable<string | null>>();

  /** The new record's key, or null when the CRM refused it. */
  create(
    action: AnswerAction,
    when: AnswerActionWhen,
  ): Observable<string | null> {
    switch (action.type) {
      case 'note':
        return this.notes.create(toNote(action));
      case 'task':
        return this.tasks.create(toTask(action, when));
      case 'appointment':
        // The organizer is the reader; v2 never fills the view store's session, the service does.
        return this.session
          .getInfo()
          .pipe(
            switchMap((session) =>
              this.appointments.create(
                toAppointment(action, when, session.user.key),
              ),
            ),
          );
    }
  }

  /** Opens a record as the chat opens any record it cites; false when the CRM does not return it. */
  open(key: string): Observable<boolean> {
    return this.timeline.forEvent(key).pipe(
      map((event) => {
        if (!event) {
          return false;
        }
        this.store.triggerOpenEntity(new OpenEntityArgs(event));
        return true;
      }),
    );
  }

  /** The entry's display name, as the CRM shows it, asked once a session; null when it has none. */
  nameOf(key: string): Observable<string | null> {
    let name = this.names.get(key);
    if (!name) {
      name = this.addressBook.getDisplayValue(key).pipe(
        // A failed lookup is asked again by the next answer that names the entry.
        catchError((error: unknown) => {
          this.names.delete(key);
          throw error;
        }),
        shareReplay(1),
      );
      this.names.set(key, name);
    }
    return name;
  }
}
